import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "care-coordination-systems",
  kind: "domain-vertical",
  title: "Care Coordination & Patient Outreach Systems",
  shortTitle: "Care Coordination",
  status: "draft",
  tier: "experience",
  seoTitle: "Care Coordination & Outreach Systems | GA",
  seoDescription:
    "Care management and patient outreach systems for care plan tracking, patient queues, encounter scheduling, and coordinated care team workflows.",
  summary:
    "Care management and patient outreach systems: structured care plans, patient work queues, encounter scheduling, and coordinated multi-role care team workflows.",
  image: "/images/blog/dashboard-design-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["healthcare"],
  relatedPostSlugs: [
    "dashboard-design",
    "operational-task-management",
    "beyond-the-hl7-firewall",
    "clinical-workflow-friction-alert-fatigue",
  ],
  engineering: [
    "Patient cohort management, outreach prioritization, and task-queue distribution",
    "Chronic care management (CCM) and annual wellness visit (AWV) workflow tooling",
    "Clinical care plan tracking, goal monitoring, and barrier documentation models",
    "Care team communication, role-based handoffs, and task escalation systems",
    "Time-tracking and clinical activity logging aligned to care-coordination standards",
    "Integration with electronic health records for demographic and schedule synchronization",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Coordinating Care Beyond Clinic Walls",
    },
    {
      type: "paragraph",
      text: "Chronic care management and proactive patient outreach require coordinating care across distributed teams of nurses, care managers, and physicians. When patient cohorts are managed through ad-hoc spreadsheets or rigid EHR task lists, outreach falls behind, care gaps remain unaddressed, and teams struggle to maintain consistent contact schedules.",
    },
    {
      type: "heading",
      level: 2,
      text: "Prioritized Queues and Structured Protocols",
    },
    {
      type: "paragraph",
      text: "Operational care coordination systems replace static rosters with dynamic work queues organized by clinical acuity, overdue tasks, and engagement milestones. By pairing structured outreach protocols with transparent time tracking, care coordinators can focus on patient engagement while maintaining the audit trails required for care program compliance.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map patient cohort workflows, care plan protocols, team handoff boundaries, and program compliance requirements.",
      produces: [
        "Care coordination workflow and patient outreach journey map",
        "Clinical cohort definition and task prioritization rules",
        "Role responsibilities and escalation path analysis",
        "Care program time-tracking and documentation obligation matrix",
      ],
      fit: {
        good: [
          "Healthcare organizations scaling chronic care management, transitional care, or population health outreach programs",
          "Care teams struggling with fragmented task management, uncoordinated patient calls, or complex documentation handoffs",
        ],
        poor: [
          "Single-provider practices needing only basic appointment calendar reminders",
          "Automated robocalling or generic bulk-SMS marketing platforms lacking clinical care protocols",
        ],
      },
    },
    "initial-design": {
      focus:
        "Architect the dynamic work queue, care plan tracking models, and multi-role coordination interfaces.",
      produces: [
        "Care coordination platform architecture and data flow diagrams",
        "Patient queue, acuity scoring, and task routing models",
        "Care plan structure, goal tracking, and barrier assessment design",
        "Time capture and encounter audit specifications",
      ],
    },
    "detailed-design": {
      focus:
        "Specify coordinator interface views, patient profile dashboards, EHR synchronization contracts, and notification rules.",
      produces: [
        "Patient work queue and care coordinator dashboard specifications",
        "Care plan editing, encounter logging, and handoff component contracts",
        "EHR demographic and appointment integration interfaces",
        "Escalation, task reassignment, and supervisor review workflows",
      ],
    },
    implementation: {
      focus:
        "Build, integrate, and validate the care coordination workflows with active care management teams under realistic operational caseloads.",
      produces: [
        "Operational care coordination and patient outreach interface suite",
        "Configured patient work queues and clinical protocol engines",
        "Automated encounter time tracking and program reporting dashboards",
        "Team operational runbooks, workflow guides, and onboarding materials",
      ],
    },
  },
};
